// Limitless AI API service layer
// Centralizes calls to the Limitless AI chat endpoint (server validates JWT + page-based access,
// then relays to the Gemini Cloud Function). This mirrors the conventions used in
// src/containers/Budget/shared/budgetApi.js.

import config from '../../config';
import Notify from '../components/widgets/Notify';

function getAuthHeaders(jwtToken) {
  return {
    ...config.jsonHeader,
    authorization: `JWT ${jwtToken}`,
  };
}

/**
 * Sends a chat message (and full transcript) to the Limitless AI backend.
 * The server is expected to:
 *   - Validate the JWT token
 *   - Confirm the user has access to the page/feature (page-driven, no plan/account check)
 *   - Compose a system prompt scoped by `scope`/`scopeDescription` so the assistant stays on-topic
 *   - Call the Gemini Cloud Function and relay back a reply plus optional actions
 *
 * @param {Object} params
 * @param {number} params.accountId - Currently selected account/seller id
 * @param {string} params.retailer - Retailer key (amazon, walmart, etc.), if applicable
 * @param {string} params.page - Page identifier that activated Limitless AI (e.g. 'budget')
 * @param {string} params.scope - Scope id used by the server to select the right guardrails/prompt
 * @param {string} params.scopeDescription - Human-readable description of what the assistant may help with
 * @param {Object} params.context - Page-specific context (field schema, current form values, etc.)
 * @param {string} params.formName - redux-form form name the assistant may update via 'set_field' actions
 * @param {Array} params.messages - Conversation transcript, e.g. [{ role: 'user', content: '...' }]
 * @param {string} params.jwtToken - JWT authentication token
 * @returns {Promise<{reply: string|null, actions: Array}>}
 */
export async function sendLimitlessAiMessage({
  accountId, retailer, page, scope, scopeDescription, context, formName, messages, jwtToken,
}) {
  const url = `${config.serverUrl}/accounts/limitless-ai/chat`;

  const requestOptions = {
    method: 'POST',
    headers: getAuthHeaders(jwtToken),
    body: JSON.stringify({
      accountId,
      retailer,
      page,
      context: {
        scope,
        scopeDescription,
        formName,
        ...context,
      },
      messages,
    }),
  };

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(response.statusText);
    }
    const result = await response.json();
    return {
      reply: result.reply || null,
      actions: Array.isArray(result.actions) ? result.actions : [],
    };
  } catch (error) {
    console.warn('[Limitless AI] chat request failed:', error.message || error);
    Notify.show('danger', 'Limitless AI', 'Limitless AI is temporarily unavailable. Please try again later.');
    return { reply: null, actions: [] };
  }
}

export default {
  sendLimitlessAiMessage,
};
